import { useState, useEffect, useRef } from "react";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { X, GraduationCap, Stethoscope } from "lucide-react";

// Education configuration data
const EDUCATION_CONFIG = {
  general_academic: {
    higherDegrees: [
      { value: "associates", label: "Associate's Degree" },
      { value: "bachelors", label: "Bachelor's Degree" },
      { value: "masters", label: "Master's Degree" },
      { value: "doctoral", label: "Doctoral (PhD/EdD)" }
    ],
    fields: [
      { value: "engineering", label: "Engineering", description: "Mechanical, Electrical, Aerospace, Civil, Chemical, Industrial, etc." },
      { value: "computer_science_it", label: "Computer Science/IT", description: "CS, Software Engineering, Information Systems, Cybersecurity" },
      { value: "business_management", label: "Business/Management", description: "Business Admin, Finance, Accounting, Marketing, HR, Operations" },
      { value: "healthcare_life_sciences", label: "Healthcare/Life Sciences", description: "Biology, Chemistry, Biochemistry, Public Health, Health Admin" },
      { value: "physical_sciences", label: "Physical Sciences", description: "Physics, Chemistry, Geology, Environmental Science" },
      { value: "mathematics_statistics", label: "Mathematics/Statistics", description: "Math, Applied Math, Statistics, Data Science, Actuarial" },
      { value: "liberal_arts", label: "Liberal Arts/Humanities", description: "Communications, English, Psychology, Sociology, Education" },
      { value: "related_field", label: "Related Field", description: "Or other related field of study" }
    ]
  },
  healthcare_clinical: {
    categories: {
      nursing: {
        label: "Nursing",
        credentials: [
          { value: "CNA", label: "CNA", description: "Certified Nursing Assistant" },
          { value: "LPN_LVN", label: "LPN/LVN", description: "Licensed Practical/Vocational Nurse" },
          { value: "ADN", label: "ADN", description: "Associate Degree in Nursing" },
          { value: "BSN", label: "BSN", description: "Bachelor of Science in Nursing" },
          { value: "MSN", label: "MSN", description: "Master of Science in Nursing" },
          { value: "DNP", label: "DNP", description: "Doctor of Nursing Practice" }
        ]
      },
      physician_medical: {
        label: "Physician/Medical Doctorate",
        credentials: [
          { value: "MD", label: "MD", description: "Doctor of Medicine" },
          { value: "DO", label: "DO", description: "Doctor of Osteopathic Medicine" },
          { value: "DDS_DMD", label: "DDS/DMD", description: "Doctor of Dental Surgery/Medicine" },
          { value: "DC", label: "D.C.", description: "Doctor of Chiropractic" },
          { value: "DPM", label: "DPM", description: "Doctor of Podiatric Medicine" },
          { value: "OD", label: "OD", description: "Doctor of Optometry" },
          { value: "PharmD", label: "PharmD", description: "Doctor of Pharmacy" },
          { value: "DVM", label: "DVM", description: "Doctor of Veterinary Medicine" }
        ]
      },
      therapy_rehabilitation: {
        label: "Therapy/Rehabilitation",
        credentials: [
          { value: "PTA", label: "PTA", description: "Physical Therapist Assistant" },
          { value: "DPT", label: "DPT", description: "Doctor of Physical Therapy" },
          { value: "COTA", label: "COTA", description: "Certified Occupational Therapy Assistant" },
          { value: "OTD_MOT", label: "OTD/MOT", description: "Occupational Therapy Doctorate/Master" },
          { value: "SLP", label: "MA/MS-SLP", description: "Speech-Language Pathology" },
          { value: "RT", label: "RT", description: "Respiratory Therapist" }
        ]
      },
      diagnostic_imaging: {
        label: "Diagnostic & Imaging",
        credentials: [
          { value: "RT_R", label: "RT(R)", description: "Radiologic Technologist" },
          { value: "CT_Tech", label: "CT Tech", description: "Computed Tomography Technologist" },
          { value: "MRI_Tech", label: "MRI Tech", description: "Magnetic Resonance Imaging Technologist" },
          { value: "Sonographer", label: "Sonographer", description: "Ultrasound Technologist" },
          { value: "Nuclear_Med", label: "Nuclear Med Tech", description: "Nuclear Medicine Technologist" },
          { value: "Mammo_Tech", label: "Mammo Tech", description: "Mammography Technologist" },
          { value: "MLT_MLS", label: "MLT/MLS", description: "Medical Laboratory Technician/Scientist" }
        ]
      }
    }
  }
};

// Helper to get field description
const getFieldDescription = (fieldValue) => {
  const field = EDUCATION_CONFIG.general_academic.fields.find(f => f.value === fieldValue);
  return field?.description || "";
};

// Helper to format degree + fields for display (returns base text and description separately)
const formatDegreeDisplay = (degree, fields) => {
  const degreeLabel = EDUCATION_CONFIG.general_academic.higherDegrees.find(d => d.value === degree)?.label || degree;
  
  // Separate primary fields from "Related Field"
  const primaryFields = fields.filter(f => f !== "related_field");
  const hasRelatedField = fields.includes("related_field");
  
  // Build field names and their descriptions
  const fieldData = primaryFields.map(f => {
    const field = EDUCATION_CONFIG.general_academic.fields.find(field => field.value === f);
    return {
      label: field?.label || f,
      description: field?.description || ""
    };
  });
  
  // Join primary fields with comma, add "or Related Field" if present
  let fieldLabels = fieldData.map(f => f.label).join(", ");
  if (hasRelatedField) {
    fieldLabels += " or Related Field";
  }
  
  // Get descriptions for fields (exclude "Related Field" description)
  const descriptions = fieldData
    .filter(f => f.description && f.label !== "Related Field")
    .map(f => f.description)
    .join("; ");
  
  return {
    base: `${degreeLabel} in ${fieldLabels}`,
    description: descriptions
  };
};

// Helper to format credential display
const formatCredentialDisplay = (category, credential) => {
  const cat = EDUCATION_CONFIG.healthcare_clinical.categories[category];
  const cred = cat?.credentials.find(c => c.value === credential);
  return cred?.label || credential;
};

export default function EducationRequirements({ value, onChange }) {
  // Initialize state from value prop or defaults
  const [track, setTrack] = useState(value?.track || "general_academic");
  
  // General/Academic state - HS/GED only for required, not preferred
  const [requiredHsGed, setRequiredHsGed] = useState(value?.required?.hsGed || false);
  const [requiredDegree, setRequiredDegree] = useState(value?.required?.degree || "");
  const [requiredFields, setRequiredFields] = useState(value?.required?.fields || []);
  const [preferredDegree, setPreferredDegree] = useState(value?.preferred?.degree || "");
  const [preferredFields, setPreferredFields] = useState(value?.preferred?.fields || []);
  
  // Healthcare/Clinical state
  const [selectedHealthcareCategory, setSelectedHealthcareCategory] = useState("");
  const [selectedPreferredCategory, setSelectedPreferredCategory] = useState("");
  const [healthcareHsGed, setHealthcareHsGed] = useState(value?.required?.hsGed || false);
  const [requiredCredentials, setRequiredCredentials] = useState(value?.required?.credentials || []);
  const [preferredCredentials, setPreferredCredentials] = useState(value?.preferred?.credentials || []);

  // Refs to prevent infinite loops
  const isInitialMount = useRef(true);
  const isLoadingFromProps = useRef(false);
  const lastValueStringRef = useRef(value ? JSON.stringify(value) : null);
  const onChangeRef = useRef(onChange);
  
  // Keep onChange ref updated
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Update state when value prop changes (for loading saved data)
  useEffect(() => {
    if (value && typeof value === 'object') {
      const currentValueString = JSON.stringify(value);
      
      // Skip if value hasn't actually changed
      if (lastValueStringRef.current === currentValueString) {
        return;
      }
      
      lastValueStringRef.current = currentValueString;
      isLoadingFromProps.current = true;
      
      setTrack(value.track || "general_academic");
      setRequiredHsGed(value.required?.hsGed || false);
      setRequiredDegree(value.required?.degree || "");
      setRequiredFields(value.required?.fields || []);
      setPreferredDegree(value.preferred?.degree || "");
      setPreferredFields(value.preferred?.fields || []);
      setHealthcareHsGed(value.required?.hsGed || false);
      setRequiredCredentials(value.required?.credentials || []);
      setPreferredCredentials(value.preferred?.credentials || []);
    }
  }, [value]);

  // Emit changes to parent
  useEffect(() => {
    // Skip initial mount to prevent unnecessary onChange call
    if (isInitialMount.current) {
      isInitialMount.current = false;
      isLoadingFromProps.current = false;
      return;
    }
    
    // Skip if we just loaded from props
    if (isLoadingFromProps.current) {
      isLoadingFromProps.current = false;
      return;
    }
    
    const educationData = {
      track,
      required: track === "general_academic" 
        ? { hsGed: requiredHsGed, degree: requiredDegree, fields: requiredFields }
        : { hsGed: healthcareHsGed, credentials: requiredCredentials },
      preferred: track === "general_academic"
        ? { degree: preferredDegree, fields: preferredFields }
        : { credentials: preferredCredentials }
    };
    
    // Update the ref to prevent loop when parent passes back the same value
    lastValueStringRef.current = JSON.stringify(educationData);
    
    // Use ref to call onChange to avoid dependency issues
    onChangeRef.current?.(educationData);
  }, [track, requiredHsGed, requiredDegree, requiredFields, preferredDegree, preferredFields, healthcareHsGed, requiredCredentials, preferredCredentials]);

  // Toggle field selection - only ONE main field allowed (plus optional Related Field)
  const toggleField = (field, section) => {
    const isRelatedField = field === "related_field";
    
    if (section === "required") {
      if (isRelatedField) {
        // Toggle Related Field independently (only if a primary field is selected)
        const hasPrimaryField = requiredFields.some(f => f !== "related_field");
        if (!hasPrimaryField) return; // Can't select Related Field without primary
        
        if (requiredFields.includes(field)) {
          setRequiredFields(requiredFields.filter(f => f !== field));
        } else {
          setRequiredFields([...requiredFields, field]);
        }
      } else {
        // For main fields: replace any existing main field (keep Related Field if selected)
        const hasRelatedField = requiredFields.includes("related_field");
        if (requiredFields.includes(field)) {
          // Deselect this field - also remove Related Field since no primary field will be selected
          setRequiredFields([]);
        } else {
          // Select this field, replacing any other main field
          const newFields = hasRelatedField ? [field, "related_field"] : [field];
          setRequiredFields(newFields);
        }
      }
    } else {
      if (isRelatedField) {
        // Toggle Related Field independently (only if a primary field is selected)
        const hasPrimaryField = preferredFields.some(f => f !== "related_field");
        if (!hasPrimaryField) return; // Can't select Related Field without primary
        
        if (preferredFields.includes(field)) {
          setPreferredFields(preferredFields.filter(f => f !== field));
        } else {
          setPreferredFields([...preferredFields, field]);
        }
      } else {
        // For main fields: replace any existing main field (keep Related Field if selected)
        const hasRelatedField = preferredFields.includes("related_field");
        if (preferredFields.includes(field)) {
          // Deselect this field - also remove Related Field since no primary field will be selected
          setPreferredFields([]);
        } else {
          // Select this field, replacing any other main field
          const newFields = hasRelatedField ? [field, "related_field"] : [field];
          setPreferredFields(newFields);
        }
      }
    }
  };

  // Toggle healthcare credential
  const toggleCredential = (category, credential, section) => {
    const credentialKey = `${category}:${credential}`;
    if (section === "required") {
      if (requiredCredentials.includes(credentialKey)) {
        setRequiredCredentials(requiredCredentials.filter(c => c !== credentialKey));
      } else {
        setRequiredCredentials([...requiredCredentials, credentialKey]);
      }
    } else {
      if (preferredCredentials.includes(credentialKey)) {
        setPreferredCredentials(preferredCredentials.filter(c => c !== credentialKey));
      } else {
        setPreferredCredentials([...preferredCredentials, credentialKey]);
      }
    }
  };

  // Remove credential badge
  const removeCredential = (credentialKey, section) => {
    if (section === "required") {
      setRequiredCredentials(requiredCredentials.filter(c => c !== credentialKey));
    } else {
      setPreferredCredentials(preferredCredentials.filter(c => c !== credentialKey));
    }
  };

  // Generate display text for output - matches output card format
  const generateDisplayText = () => {
    if (track === "general_academic") {
      // Get all unique fields across required and preferred - descriptions shown once at end
      const allUniqueFields = [...new Set([...requiredFields, ...preferredFields])];
      const allDescriptions = allUniqueFields
        .filter(f => f !== "related_field")
        .map(f => {
          const field = EDUCATION_CONFIG.general_academic.fields.find(field => field.value === f);
          return field?.description || "";
        })
        .filter(d => d);
      
      let result = "";
      
      // Build required part (no inline descriptions)
      if (requiredHsGed || (requiredDegree && requiredDegree !== "none" && requiredFields.length > 0)) {
        result = "Candidates must hold ";
        
        const requiredParts = [];
        if (requiredHsGed) {
          requiredParts.push("a High School diploma/GED");
        }
        if (requiredDegree && requiredDegree !== "none" && requiredFields.length > 0) {
          const formatted = formatDegreeDisplay(requiredDegree, requiredFields);
          requiredParts.push(`a ${formatted.base}`);
        }
        
        result += requiredParts.join(" and ");
      }
      
      // Build preferred part (no inline descriptions)
      if (preferredDegree && preferredDegree !== "none" && preferredFields.length > 0) {
        const formatted = formatDegreeDisplay(preferredDegree, preferredFields);
        if (result) {
          result += ", with a " + formatted.base + " preferred";
        } else {
          result = formatted.base + " preferred";
        }
      }
      
      // Add ALL descriptions ONCE at the very end
      if (allDescriptions.length > 0 && result) {
        result += ` (${allDescriptions.join("; ")})`;
      }
      
      return result ? result + "." : "";
    } else {
      // Healthcare/Clinical track - sentence format
      let result = "";
      
      // Build required part
      if (healthcareHsGed || requiredCredentials.length > 0) {
        result = "Candidates must hold ";
        
        const requiredParts = [];
        if (healthcareHsGed) {
          requiredParts.push("a High School diploma/GED");
        }
        if (requiredCredentials.length > 0) {
          const labels = requiredCredentials.map(c => {
            const [cat, cred] = c.split(":");
            return formatCredentialDisplay(cat, cred);
          });
          requiredParts.push(labels.join(" or ") + " certification");
        }
        
        result += requiredParts.join(" and ");
      }
      
      // Build preferred part
      if (preferredCredentials.length > 0) {
        const labels = preferredCredentials.map(c => {
          const [cat, cred] = c.split(":");
          return formatCredentialDisplay(cat, cred);
        });
        if (result) {
          result += ", with " + labels.join(" or ") + " preferred";
        } else {
          result = labels.join(" or ") + " preferred";
        }
      }
      
      return result ? result + "." : "";
    }
  };

  return (
    <div className="space-y-4">
      <Label className="text-base font-semibold">Education Requirements</Label>
      
      {/* Track Selection */}
      <RadioGroup value={track} onValueChange={setTrack} className="flex gap-4">
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="general_academic" id="track-academic" />
          <Label htmlFor="track-academic" className="flex items-center gap-2 cursor-pointer">
            <GraduationCap className="h-4 w-4" />
            General/Academic
          </Label>
        </div>
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="healthcare_clinical" id="track-healthcare" />
          <Label htmlFor="track-healthcare" className="flex items-center gap-2 cursor-pointer">
            <Stethoscope className="h-4 w-4" />
            Healthcare/Clinical
          </Label>
        </div>
      </RadioGroup>

      {/* General/Academic Track */}
      {track === "general_academic" && (
        <div className="space-y-4">
          {/* Required Section */}
          <Card>
            <CardContent className="pt-4">
              <Label className="text-sm font-medium text-green-700">Required Education</Label>
              <div className="mt-3 space-y-3">
                {/* HS/GED Checkbox */}
                <div className="flex items-center space-x-2 pb-2 border-b">
                  <Checkbox
                    id="req-hs-ged"
                    checked={requiredHsGed}
                    onCheckedChange={setRequiredHsGed}
                  />
                  <Label htmlFor="req-hs-ged" className="text-sm cursor-pointer font-medium">
                    High School/GED
                  </Label>
                </div>

                {/* Higher Degree Dropdown */}
                <div>
                  <Label className="text-xs text-muted-foreground">Higher Degree Level</Label>
                  <Select value={requiredDegree} onValueChange={setRequiredDegree}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select higher degree level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {EDUCATION_CONFIG.general_academic.higherDegrees.map(deg => (
                        <SelectItem key={deg.value} value={deg.value}>{deg.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {requiredDegree && requiredDegree !== "none" && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Field(s) of Study</Label>
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {EDUCATION_CONFIG.general_academic.fields.map(field => {
                        // Check if this is "Related Field" - it requires a primary field to be selected first
                        const isRelatedField = field.value === "related_field";
                        const hasPrimaryFieldSelected = requiredFields.some(f => f !== "related_field");
                        const isDisabled = isRelatedField && !hasPrimaryFieldSelected;
                        
                        return (
                          <div key={field.value} className={`flex items-start space-x-2 ${isDisabled ? 'opacity-50' : ''}`}>
                            <Checkbox
                              id={`req-${field.value}`}
                              checked={requiredFields.includes(field.value)}
                              onCheckedChange={() => !isDisabled && toggleField(field.value, "required")}
                              disabled={isDisabled}
                            />
                            <div className="grid gap-0.5 leading-none">
                              <Label htmlFor={`req-${field.value}`} className={`text-sm ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                {field.label}
                              </Label>
                              <span className="text-xs text-muted-foreground">
                                {isRelatedField && !hasPrimaryFieldSelected 
                                  ? "Select a primary field first" 
                                  : field.description}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Preferred Section */}
          <Card>
            <CardContent className="pt-4">
              <Label className="text-sm font-medium text-blue-700">Preferred Education</Label>
              <div className="mt-3 space-y-3">
                {/* Higher Degree Dropdown */}
                <div>
                  <Label className="text-xs text-muted-foreground">Higher Degree Level</Label>
                  <Select value={preferredDegree} onValueChange={setPreferredDegree}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select higher degree level (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {EDUCATION_CONFIG.general_academic.higherDegrees.map(deg => (
                        <SelectItem key={deg.value} value={deg.value}>{deg.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {preferredDegree && preferredDegree !== "none" && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Field(s) of Study</Label>
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {EDUCATION_CONFIG.general_academic.fields.map(field => {
                        // Check if this is "Related Field" - it requires a primary field to be selected first
                        const isRelatedField = field.value === "related_field";
                        const hasPrimaryFieldSelected = preferredFields.some(f => f !== "related_field");
                        const isDisabled = isRelatedField && !hasPrimaryFieldSelected;
                        
                        return (
                          <div key={field.value} className={`flex items-start space-x-2 ${isDisabled ? 'opacity-50' : ''}`}>
                            <Checkbox
                              id={`pref-${field.value}`}
                              checked={preferredFields.includes(field.value)}
                              onCheckedChange={() => !isDisabled && toggleField(field.value, "preferred")}
                              disabled={isDisabled}
                            />
                            <div className="grid gap-0.5 leading-none">
                              <Label htmlFor={`pref-${field.value}`} className={`text-sm ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                {field.label}
                              </Label>
                              <span className="text-xs text-muted-foreground">
                                {isRelatedField && !hasPrimaryFieldSelected 
                                  ? "Select a primary field first" 
                                  : field.description}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Healthcare/Clinical Track */}
      {track === "healthcare_clinical" && (
        <div className="space-y-4">
          {/* Required Section */}
          <Card>
            <CardContent className="pt-4">
              <Label className="text-sm font-medium text-green-700">Required Credentials</Label>
              <div className="mt-3 space-y-3">
                {/* HS/GED Checkbox */}
                <div className="flex items-center space-x-2 pb-2 border-b">
                  <Checkbox
                    id="healthcare-hs-ged"
                    checked={healthcareHsGed}
                    onCheckedChange={setHealthcareHsGed}
                  />
                  <Label htmlFor="healthcare-hs-ged" className="text-sm cursor-pointer font-medium">
                    High School/GED
                  </Label>
                </div>

                <div>
                  <Label className="text-xs text-muted-foreground">Select Category</Label>
                  <Select value={selectedHealthcareCategory} onValueChange={setSelectedHealthcareCategory}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select healthcare category" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(EDUCATION_CONFIG.healthcare_clinical.categories).map(([key, cat]) => (
                        <SelectItem key={key} value={key}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedHealthcareCategory && (
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      {EDUCATION_CONFIG.healthcare_clinical.categories[selectedHealthcareCategory].label} Credentials
                    </Label>
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {EDUCATION_CONFIG.healthcare_clinical.categories[selectedHealthcareCategory].credentials.map(cred => {
                        const credKey = `${selectedHealthcareCategory}:${cred.value}`;
                        return (
                          <div key={cred.value} className="flex items-start space-x-2">
                            <Checkbox
                              id={`req-${credKey}`}
                              checked={requiredCredentials.includes(credKey)}
                              onCheckedChange={() => toggleCredential(selectedHealthcareCategory, cred.value, "required")}
                            />
                            <div className="grid gap-0.5 leading-none">
                              <Label htmlFor={`req-${credKey}`} className="text-sm cursor-pointer font-medium">
                                {cred.label}
                              </Label>
                              <span className="text-xs text-muted-foreground">{cred.description}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Selected Required Badges */}
                {requiredCredentials.length > 0 && (
                  <div className="pt-2">
                    <Label className="text-xs text-muted-foreground">Selected Required:</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {requiredCredentials.map(credKey => {
                        const [cat, cred] = credKey.split(":");
                        return (
                          <Badge key={credKey} variant="secondary" className="gap-1">
                            {formatCredentialDisplay(cat, cred)}
                            <X 
                              className="h-3 w-3 cursor-pointer hover:text-destructive" 
                              onClick={() => removeCredential(credKey, "required")}
                            />
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Preferred Section */}
          <Card>
            <CardContent className="pt-4">
              <Label className="text-sm font-medium text-blue-700">Preferred Credentials</Label>
              <div className="mt-3 space-y-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Select Category</Label>
                  <Select value={selectedPreferredCategory} onValueChange={setSelectedPreferredCategory}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select healthcare category" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(EDUCATION_CONFIG.healthcare_clinical.categories).map(([key, cat]) => (
                        <SelectItem key={key} value={key}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedPreferredCategory && (
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      {EDUCATION_CONFIG.healthcare_clinical.categories[selectedPreferredCategory].label} Credentials
                    </Label>
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {EDUCATION_CONFIG.healthcare_clinical.categories[selectedPreferredCategory].credentials.map(cred => {
                        const credKey = `${selectedPreferredCategory}:${cred.value}`;
                        return (
                          <div key={cred.value} className="flex items-start space-x-2">
                            <Checkbox
                              id={`pref-${credKey}`}
                              checked={preferredCredentials.includes(credKey)}
                              onCheckedChange={() => toggleCredential(selectedPreferredCategory, cred.value, "preferred")}
                            />
                            <div className="grid gap-0.5 leading-none">
                              <Label htmlFor={`pref-${credKey}`} className="text-sm cursor-pointer font-medium">
                                {cred.label}
                              </Label>
                              <span className="text-xs text-muted-foreground">{cred.description}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Selected Preferred Badges */}
                {preferredCredentials.length > 0 && (
                  <div className="pt-2">
                    <Label className="text-xs text-muted-foreground">Selected Preferred:</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {preferredCredentials.map(credKey => {
                        const [cat, cred] = credKey.split(":");
                        return (
                          <Badge key={credKey} variant="outline" className="gap-1">
                            {formatCredentialDisplay(cat, cred)}
                            <X 
                              className="h-3 w-3 cursor-pointer hover:text-destructive" 
                              onClick={() => removeCredential(credKey, "preferred")}
                            />
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Preview */}
      {generateDisplayText() && (
        <div className="p-3 bg-muted rounded-md">
          <Label className="text-xs text-muted-foreground">Preview:</Label>
          <p className="text-sm mt-1">{generateDisplayText()}</p>
        </div>
      )}
    </div>
  );
}

// Export config for use in other components
export { EDUCATION_CONFIG, getFieldDescription, formatDegreeDisplay };
